import {
  ArrowDown,
  Award,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Filter,
  MapPin,
  MessageSquare,
  Search,
  ShieldCheck,
  Star,
  Wallet,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../data/ownerPrototype';
import { useOwnerManpro } from '../../context/OwnerManproContext';
import { manproQueryKeys, ownerBidService, type BidStatus, type OwnerProjectBidDto, type OwnerProjectDto } from '../../../api/manpro';
import { useQuery } from '@tanstack/react-query';

type BidInboxRow = {
  bid: OwnerProjectBidDto;
  project: OwnerProjectDto;
  rating: number;
  reviews: number;
  verified: boolean;
};

const statusTone: Record<BidStatus, string> = {
  pending: 'bg-blue-50 text-blue-700',
  submitted: 'bg-blue-50 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-800',
  denied: 'bg-red-50 text-red-700',
  rejected: 'bg-red-50 text-red-700',
  withdrawn: 'bg-slate-100 text-slate-500',
};

const statusLabel: Record<BidStatus, string> = {
  pending: 'Perlu Review',
  submitted: 'Perlu Review',
  accepted: 'Diterima',
  denied: 'Ditolak',
  rejected: 'Ditolak',
  withdrawn: 'Ditarik Provider',
};

const getRelativeSubmittedAt = (submittedAt?: string) => {
  if (!submittedAt) return 'baru saja';

  const diffMs = Date.now() - new Date(submittedAt).getTime();
  const diffHours = Math.max(0, Math.floor(diffMs / 1000 / 60 / 60));

  if (diffHours < 1) return 'baru saja';
  if (diffHours < 24) return `${diffHours} jam yang lalu`;

  return `${Math.floor(diffHours / 24)} hari yang lalu`;
};

const getVendorScore = (providerName: string) => {
  const seed = providerName.split('').reduce((total, char) => total + char.charCodeAt(0), 0);

  return {
    rating: Number((4.6 + (seed % 4) / 10).toFixed(1)),
    reviews: 48 + (seed % 120),
    verified: seed % 3 !== 0,
  };
};

const bidDocumentCategoryLabels: Record<string, string> = {
  portfolio: 'Portfolio',
  company_profile: 'Company Profile',
  rab: 'RAB / Breakdown',
  method: 'Metode Kerja',
  legal: 'Legalitas',
  other: 'Lainnya',
};

const getProviderName = (bid: OwnerProjectBidDto) =>
  bid.provider?.company_name ||
  bid.provider?.name ||
  bid.provider?.contact_person_name ||
  'Provider';

const getBidAmount = (bid: OwnerProjectBidDto) => bid.amount ?? bid.offered_price ?? 0;

const getBidNote = (bid: OwnerProjectBidDto) =>
  bid.note || bid.provider_note || 'Provider mengirim penawaran untuk proyek ini.';

const isReviewableBid = (bid: OwnerProjectBidDto) => ['submitted', 'pending'].includes(bid.status);

const PenawaranMasuk = () => {
  const navigate = useNavigate();
  const {
    acceptBid,
    rejectBid,
    isAcceptingBid,
    isRejectingBid,
    projectSummary,
  } = useOwnerManpro();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [statusFilter, setStatusFilter] = useState('');

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // reset to page 1 on new search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const bidsQuery = useQuery({
    queryKey: manproQueryKeys.ownerAllBids({ page, limit: 10, search: debouncedSearch, sort, status: statusFilter } as any),
    queryFn: () => ownerBidService.getAllOwnerBids({ page, limit: 10, search: debouncedSearch, sort, status: statusFilter } as any),
  });

  const bidsData = bidsQuery.data;
  const bidList = bidsData?.data || [];
  const isLoadingBids = bidsQuery.isLoading || bidsQuery.isFetching;

  const bidRows = useMemo<BidInboxRow[]>(
    () =>
      bidList.map((bid: any) => ({
        bid,
        project: bid.project || {} as any,
        ...getVendorScore(getProviderName(bid)),
      })),
    [bidList],
  );



  const updateBidStatus = async (projectId: string, bidId: string, status: 'accepted' | 'rejected') => {
    if (status === 'accepted') {
      await acceptBid(projectId, bidId);
      navigate(`/owner/proyek-saya/${projectId}`);
      return;
    }

    await rejectBid(projectId, bidId);
  };

  const totalSubmitted = bidRows.filter((row) => isReviewableBid(row.bid)).length;
  const averageRating =
    bidRows.length > 0
      ? (bidRows.reduce((total, row) => total + row.rating, 0) / bidRows.length).toFixed(1)
      : '0.0';

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Penawaran Masuk</h1>
          <p className="mt-2 text-sm text-slate-500">
            Review proposal vendor dari proyek Owner, pilih partner terbaik, lalu buka Monitoring Owner setelah bid diterima.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            [String(bidsData?.total || 0), 'Total penawaran'],
            [String(totalSubmitted), 'Belum direview'],
            [averageRating, 'Rata-rata rating'],
          ].map(([value, label]) => (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <p className="text-2xl font-extrabold text-slate-900">{value}</p>
              <p className="mt-1 text-xs font-semibold text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_310px]">
        <div className="min-w-0 space-y-4">
          <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 lg:grid-cols-[minmax(260px,1fr)_140px_140px_120px]">
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-500">
              <Search size={17} />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full bg-transparent outline-none"
                placeholder="Cari vendor, proyek, kategori..."
              />
            </label>
            <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 px-3 py-2.5 text-sm font-bold text-blue-700">
              <Filter size={16} />
              Filter
            </button>
            <button 
              onClick={() => { setSort(sort === 'newest' ? 'oldest' : 'newest'); setPage(1); }} 
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold text-slate-600"
            >
              {sort === 'newest' ? 'Terbaru' : 'Terlama'}
              <ArrowDown size={16} className={sort === 'oldest' ? 'rotate-180' : ''} />
            </button>
            <button className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm shadow-blue-100">
              Bandingkan
            </button>
          </div>

          {isLoadingBids ? <div className="py-20 text-center font-bold text-slate-500">Memuat penawaran...</div> : bidRows.map(({ bid, project, rating, reviews, verified }) => {
            const providerName = getProviderName(bid);

            return (
            <article key={`${project.id}-${bid.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-blue-100 hover:shadow-md">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_310px]">
                <div className="flex min-w-0 flex-col">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-extrabold ${statusTone[bid.status]}`}>
                      {statusLabel[bid.status]}
                    </span>
                    {verified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-extrabold text-blue-700">
                        <ShieldCheck size={14} />
                        Verified Vendor
                      </span>
                    )}
                    {project.accepted_post_bid_id === bid.id && (
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-extrabold text-emerald-700">
                        Provider Terpilih
                      </span>
                    )}
                  </div>

                  <h2 className="mt-3 text-xl font-extrabold text-slate-900">{providerName}</h2>
                  <Link to={`/owner/proyek-saya/${project.id}`} className="mt-1 text-sm font-semibold text-blue-700 hover:underline">
                    {project.title}
                  </Link>
                  <p className="mt-3 flex-1 text-sm leading-6 text-slate-500">{getBidNote(bid)}</p>

                  <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-2 xl:grid-cols-4">
                    <span className="inline-flex items-center gap-2">
                      <MapPin size={16} className="text-slate-400" />
                      {project.location || project.city || '-'}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Building2 size={16} className="text-slate-400" />
                      {project.category || '-'}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Clock size={16} className="text-slate-400" />
                      {bid.duration || '-'}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Star size={16} className="fill-amber-400 text-amber-400" />
                      {rating} ({reviews})
                    </span>
                  </div>

                  {bid.documents?.length ? (
                    <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-3">
                      <p className="text-xs font-extrabold text-slate-700">Dokumen Pendukung Provider</p>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {bid.documents.slice(0, 4).map((document) => (
                          <div key={document.id} className="min-w-0 rounded-xl bg-white px-3 py-2">
                            <p className="truncate text-xs font-extrabold text-slate-800">{document.name}</p>
                            <p className="mt-1 text-[11px] font-semibold text-slate-500">
                              {bidDocumentCategoryLabels[document.category || 'other'] || 'Dokumen'}
                            </p>
                          </div>
                        ))}
                      </div>
                      {bid.documents.length > 4 && (
                        <p className="mt-2 text-xs font-bold text-blue-700">+{bid.documents.length - 4} dokumen lainnya</p>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-400">
                      Provider belum melampirkan dokumen portfolio/RAB.
                    </div>
                  )}
                </div>

                <div className="flex flex-col rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-500">Nilai Penawaran</p>
                    <p className="mt-1 text-xl font-extrabold text-slate-900">{formatCurrency(getBidAmount(bid))}</p>
                    <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                      <Calendar size={14} />
                      Masuk {getRelativeSubmittedAt(bid.submitted_at || undefined)}
                    </p>
                  </div>

                  <div className="mt-4 grid gap-2">
                    <Link
                      to={`/owner/penawaran-masuk/${project.id}/${bid.id}`}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2.5 text-sm font-extrabold text-blue-700 transition hover:bg-blue-50"
                    >
                      <FileText size={16} />
                      Lihat Detail
                    </Link>
                    <button
                      onClick={() => updateBidStatus(project.id, bid.id, 'accepted')}
                      disabled={!isReviewableBid(bid) || isAcceptingBid}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-extrabold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      <CheckCircle2 size={16} />
                      Terima
                    </button>
                    <button
                      onClick={() => updateBidStatus(project.id, bid.id, 'rejected')}
                      disabled={!isReviewableBid(bid) || isRejectingBid}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 bg-white px-4 py-2.5 text-sm font-extrabold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300"
                    >
                      <XCircle size={16} />
                      Tolak
                    </button>
                  </div>
                </div>
              </div>
            </article>
            );
          })}

          {!isLoadingBids && bidRows.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <FileText size={38} className="mx-auto text-slate-300" />
              <p className="mt-3 font-bold text-slate-700">Belum ada penawaran masuk</p>
              <p className="mt-1 text-sm text-slate-400">Penawaran dari provider akan muncul otomatis setelah mereka mengirim bid.</p>
            </div>
          )}
        </div>

        <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-[310px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Checklist Review B2B</h2>
            <div className="mt-4 space-y-3">
              {['Legalitas vendor', 'Metode kerja', 'RAB dan termin', 'Durasi pelaksanaan', 'Garansi pekerjaan'].map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                  <CheckCircle2 size={17} className="text-emerald-600" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <Award size={28} className="text-blue-700" />
            <h2 className="mt-3 font-extrabold text-slate-900">Rekomendasi</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Setelah provider diterima, proyek otomatis masuk MANPRO dan Owner diarahkan ke halaman monitoring.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <Wallet size={26} className="text-blue-700" />
            <h2 className="mt-3 font-extrabold text-slate-900">Alur setelah diterima</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Owner cukup memantau. Provider Merchant melanjutkan baseline, delivery, validasi, dan laporan di workspace operasional.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default PenawaranMasuk;
